/**
 * NasSyncPanel — Bloc 2 de la section "Sauvegardes & Stockage externe".
 *
 * Spec 2026-07-23 : affiche l'état du miroir NAS Synology alimenté par les
 * rapports périodiques (POST /api/nas-sync/report). Aucun accès entrant du
 * backend vers le NAS — toute donnée provient du dernier rapport reçu.
 *
 * Fonctionnalités :
 *   - KPI pills : espace utilisé, nombre de fichiers, date du dernier fichier.
 *   - Badge de fraîcheur (fresh <24h · stale 24-48h · missing >48h).
 *   - Édition libre du lien QuickConnect + bouton "Ouvrir sur le NAS ↗".
 *   - Section technique repliable : hint clé, régénération, endpoint URL,
 *     copie du script curl prêt à coller dans Synology Task Scheduler.
 *   - Modale one-shot affichant la clé en clair après (re)génération.
 */
import { hasRight } from "@/components/crm/crmShared";
import { useEffect, useMemo, useState } from "react";
import { toast } from "@/lib/toast";
import {
  Server, Copy, RefreshCw, ExternalLink, ChevronDown, ChevronUp,
  Wrench, ShieldAlert, ShieldCheck, ShieldQuestion, KeyRound, Terminal,
  FileClock, Files, HardDrive, Save, AlertOctagon, CheckCircle2,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import DeskConfirm from "@/components/crm/DeskConfirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Secret } from "@/lib/privacy";

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  const u = ["o", "Ko", "Mo", "Go"];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 2 : 1)} ${u[i]}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const fmtRelative = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffMin = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
    if (diffMin < 1) return "à l'instant";
    if (diffMin < 60) return `il y a ${diffMin} min`;
    const h = Math.round(diffMin / 60);
    if (h < 48) return `il y a ${h} h`;
    const days = Math.round(h / 24);
    return `il y a ${days} j`;
  } catch { return iso; }
};

const copy = async (text, label = "Copié") => {
  try {
    await navigator.clipboard.writeText(text || "");
    toast.success(label);
  } catch {
    toast.error("Copie impossible");
  }
};

const badgeIcon = (status) => {
  if (status === "fresh") return <ShieldCheck size={13} />;
  if (status === "stale") return <ShieldAlert size={13} />;
  if (status === "missing") return <ShieldAlert size={13} />;
  return <ShieldQuestion size={13} />;
};

export default function NasSyncPanel() {
  const { user } = useAuth();
  const canWrite = hasRight(user, "settings.nas"); // iter203 — droit de la matrice (plus de premier-admin en dur)
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [qcInput, setQcInput] = useState("");
  const [savingQc, setSavingQc] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [showTech, setShowTech] = useState(false);

  // Modale one-shot "clé secrète créée / régénérée"
  const [revealScript, setRevealScript] = useState(false);
  const [oneShot, setOneShot] = useState(null); // {secret, endpoint, curl_snippet, is_regeneration}
  const [script, setScript] = useState(null); // spec iter317 — {version, script, has_plain_secret}
  const [testing, setTesting] = useState(false);   // spec iter319 — « Tester la réception »
  const [testResult, setTestResult] = useState(null);
  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/nas-sync/test-report");
      setTestResult(data);
      if (data.ok) load();
    } catch (err) {
      setTestResult({ ok: false, step: "http", http: err.response?.status || 0, detail: err.response?.data?.detail || err.message });
    } finally { setTesting(false); }
  };
  const loadScript = () => api.get("/nas-sync/script").then(({ data }) => setScript(data)).catch(() => {});
  const scrollToScript = () => {
    setShowTech(true);
    setTimeout(() => document.getElementById("nas-script-block")?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/nas-sync/state");
      setState(data);
      setQcInput(data.quickconnect_url || "");
      loadScript();
    } catch (err) {
      toast.error("Chargement impossible", { description: err.response?.data?.detail || "" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [askRotate, setAskRotate] = useState(false); // iter387 — remplace window.confirm
  const rotate = async () => {
    if (state?.has_secret && !askRotate) { setAskRotate(true); return; }
    setAskRotate(false);
    setRotating(true);
    try {
      // Spec 2026-08-01 — on passe explicitement l'origine visible côté
      // navigateur pour que le script généré pointe vers le domaine sur
      // lequel l'admin est réellement connecté (vert-tec.com en prod).
      const { data } = await api.post("/nas-sync/rotate-secret", {
        origin: window.location.origin,
      });
      setOneShot({ ...data, is_regeneration: !!state?.has_secret });
      loadScript();
      await load();
    } catch (err) {
      toast.error("Génération impossible", { description: err.response?.data?.detail || "" });
    } finally { setRotating(false); }
  };

  const saveQc = async () => {
    setSavingQc(true);
    try {
      await api.put("/nas-sync/quickconnect", { url: qcInput.trim() });
      toast.success("Lien QuickConnect enregistré");
      await load();
    } catch (err) {
      toast.error("Enregistrement impossible", { description: err.response?.data?.detail || "" });
    } finally { setSavingQc(false); }
  };

  const freshness = state?.freshness || { status: "never", label: "—", color: "#94a3b8" };
  const report = state?.last_report;

  const endpointFull = useMemo(() => {
    if (!state?.endpoint_path) return "";
    // Le backend renvoie juste le chemin — on l'ajoute au host courant pour affichage.
    return window.location.origin + state.endpoint_path;
  }, [state]);

  // Spec 2026-08-01 — garde-fou : si l'admin visite Le Desk via l'URL preview,
  // toute clé/script généré ici pointera vers un domaine bloqué par le WAF
  // Emergent (403 en amont, jamais reçu par l'app). On avertit clairement.
  const isPreviewHost = useMemo(() => {
    const h = (window.location.host || "").toLowerCase();
    return h.includes(".emergentagent.com") || h.includes(".emergentcf.cloud");
  }, []);

  if (loading || !state) {
    return (
      <Card className="p-6 bg-card border-vertec-borderDark text-sm text-muted-foreground">
        Chargement du miroir NAS…
      </Card>
    );
  }

  return (
    <Card className="p-0 bg-card border-vertec-borderDark overflow-hidden" data-testid="nas-sync-panel">
      <DeskConfirm open={askRotate} title="Régénérer la clé secrète NAS ?" message="L'ancienne clé sera invalidée immédiatement : le script sur le NAS Synology cessera de fonctionner tant que la nouvelle clé n'y est pas mise à jour." confirmLabel="Régénérer" onClose={() => setAskRotate(false)} onConfirm={rotate} testId="nas-rotate-confirm" />
      {!canWrite && (
        <div
          className="flex items-center gap-2 px-6 py-2.5 text-[13px] border-b border-vertec-borderDark"
          style={{
            background: "#17171B",
            borderBottom: "1px solid #26262C",
            boxShadow: "inset 3px 0 0 0 #3B82F6",
            color: "#93C5FD",
          }}
          data-testid="nas-readonly-banner"
        >
          <ShieldQuestion size={14} />
          <span>
            Mode <strong>lecture seule</strong> — le Premier Admin seul peut modifier le
            lien QuickConnect ou régénérer la clé secrète NAS.
          </span>
        </div>
      )}
      {/* Header avec badge fraîcheur */}
      <div className="px-6 pt-5 pb-4 border-b border-vertec-borderDark flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="rounded-lg flex items-center justify-center shrink-0"
               style={{ width: 44, height: 44, background: "#141417", border: "1px solid #26262C", color: "#8B5CF6" }}>
            <Server size={22} />
          </div>
          <div>
            <h3 className="text-lg font-semibold">NAS Synology <span className="text-sm text-muted-foreground font-normal">(miroir local des sauvegardes B2)</span></h3>
            <p className="text-xs text-muted-foreground max-w-2xl mt-1">
              Copie locale des archives ZIP <code>_backups/</code> hébergées sur Backblaze B2, dans <code>/volume1/LeDesk-B2</code>.
              La rétention peut être plus longue côté NAS que côté B2 (backups anciens conservés localement même après purge cloud).
              L'écart de fichiers avec B2 = backups archivés hors rotation ou en attente de purge locale.
              <span className="block mt-1 opacity-80">
                La synchro est initiée depuis Synology Task Scheduler — le backend n'a aucun accès entrant.
              </span>
            </p>
          </div>
        </div>
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs shrink-0"
          style={{
            background: "transparent",
            color: freshness.color,
            border: `1px solid ${freshness.color}80`,
          }}
          data-testid={`nas-freshness-${freshness.status}`}
        >
          {badgeIcon(freshness.status)}
          <span className="font-semibold">{freshness.label}</span>
          {report?.reported_at && (
            <span className="text-muted-foreground">· {fmtRelative(report.reported_at)}</span>
          )}
        </div>
      </div>

      {/* KPI pills */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 px-6 pt-5">
        <div className="rounded-lg p-4 flex items-center gap-3"
             style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
             data-testid="nas-kpi-used">
          <HardDrive size={20} className="text-muted-foreground" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Espace utilisé</div>
            <div className="text-xl font-semibold tabular-nums mt-0.5">{fmtBytes(report?.used_bytes)}</div>
          </div>
        </div>
        <div className="rounded-lg p-4 flex items-center gap-3"
             style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
             data-testid="nas-kpi-count">
          <Files size={20} className="text-muted-foreground" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Fichiers</div>
            <div className="text-xl font-semibold tabular-nums mt-0.5">{report ? (report.files_count ?? "—") : "—"}</div>
          </div>
        </div>
        <div className="rounded-lg p-4 flex items-center gap-3"
             style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
             data-testid="nas-kpi-last-file">
          <FileClock size={20} className="text-muted-foreground" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Dernier fichier</div>
            <div className="text-sm font-semibold mt-0.5">{fmtDate(report?.last_file_at)}</div>
          </div>
        </div>
      </div>

      {/* Écart B2 → NAS (spec iter288) */}
      {state?.sync_gap?.stale && (
        <div className="mx-6 mt-4 rounded-lg p-4 text-xs space-y-2"
             style={{ background: "#17171B", border: "1px solid #26262C", borderLeft: "3px solid #F5C842" }}
             data-testid="nas-sync-gap-alert">
          <div className="font-semibold text-[#F5C842]">
            Le NAS n'a pas récupéré les derniers backups B2 ({Math.round(state.sync_gap.hours / 24)} j d'écart)
          </div>
          <div className="text-muted-foreground">
            Dernier backup B2 : <strong className="text-foreground">{fmtDate(state.last_b2_backup?.created_at)}</strong>
            {" · "}{state.last_b2_backup?.filename} ({fmtBytes(state.last_b2_backup?.size)})
            {" — "}dernier fichier vu par le NAS : <strong className="text-foreground">{fmtDate(report?.last_file_at)}</strong>.
          </div>
          <div className="text-muted-foreground">
            Le Desk génère bien ses backups : le blocage est côté Synology Cloud Sync. À vérifier sur le NAS :
            tâche Cloud Sync active (non en pause / erreur), dossier distant <code>_backups/</code> et dossier local
            identique à <code>{report?.backup_folder || "BACKUP_DIR"}</code>, filtre « taille max de fichier » (les ZIP font ~{fmtBytes(state.last_b2_backup?.size)}),
            espace disque du volume, et journal Cloud Sync.
          </div>
        </div>
      )}

      {/* Métadonnées rapport */}
      {report && (
        <div className="px-6 mt-4 text-xs text-muted-foreground space-y-1">
          <div>
            <span className="text-[10px] uppercase tracking-wider">Dernier rapport reçu :</span>{" "}
            <strong className="text-foreground">{fmtDate(report.reported_at)}</strong>
          </div>
          {/* Spec iter310 — version du script installé sur le NAS vs dernière disponible. */}
          <div data-testid="nas-script-version">
            <span className="text-[10px] uppercase tracking-wider">Script NAS :</span>{" "}
            <strong className="text-foreground">v{report.script_version || 1}</strong>
            <span className="text-muted-foreground"> · dernière disponible v{state?.script_version_latest}</span>
            {state?.script_outdated ? (
              <span className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold"
                style={{ background: "transparent", color: "#F5C842", border: "1px solid rgba(245,200,66,0.5)" }}
                data-testid="nas-script-outdated">Obsolète — <button type="button" onClick={scrollToScript} className="underline hover:text-white" data-testid="nas-script-outdated-link">re-copier le script ci-dessous</button>{" "}dans le Planificateur Synology</span>
            ) : (
              <span className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold"
                style={{ background: "transparent", color: "#22C55E", border: "1px solid rgba(34,197,94,0.5)" }}
                data-testid="nas-script-uptodate">À jour</span>
            )}
          </div>
          {(report.hostname || report.source_ip) && (
            <div>
              <span className="text-[10px] uppercase tracking-wider">Source :</span>{" "}
              <Secret value={`${report.source_ip || "—"}${report.hostname ? ` (${report.hostname})` : ""}`} superAdmin={canWrite} testId="nas-secret-source" />
            </div>
          )}
          {report.backup_folder && (
            <div>
              <span className="text-[10px] uppercase tracking-wider">Dossier NAS :</span>{" "}
              <code className="text-foreground bg-vertec-borderDark/40 px-1.5 py-0.5 rounded text-[11px]">{report.backup_folder}</code>
            </div>
          )}
          {report.last_file_name && (
            <div>
              <span className="text-[10px] uppercase tracking-wider">Dernier ZIP :</span>{" "}
              <code className="text-foreground bg-vertec-borderDark/40 px-1.5 py-0.5 rounded text-[11px]">{report.last_file_name}</code>
            </div>
          )}
        </div>
      )}
      {!report && (
        <div className="px-6 mt-4 text-xs text-muted-foreground italic">
          Aucun rapport reçu à ce jour. Configurez d&apos;abord la clé secrète ci-dessous, puis programmez la tâche planifiée sur votre NAS.
        </div>
      )}

      {/* QuickConnect */}
      <div className="mx-6 my-5 rounded-lg p-4"
           style={{ background: "#17171B", border: "1px solid #26262C", borderLeft: "3px solid #0078D4" }}>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 block">
          Lien QuickConnect (accès direct au dossier NAS)
        </Label>
        <div className="flex items-center gap-2 flex-wrap">
          {canWrite ? <Input
            value={qcInput}
            onChange={(e) => setQcInput(e.target.value)}
            placeholder="https://vertec.quickconnect.to/file_station/…"
            className="flex-1 min-w-[280px] bg-transparent border-vertec-borderDark"
            data-testid="nas-quickconnect-input"
          /> : <Secret value={state.quickconnect_url} superAdmin={false} testId="nas-secret-quickconnect" />}
          <Button
            variant="outline" size="sm" className="gap-1.5 border-vertec-borderDark"
            onClick={saveQc}
            disabled={!canWrite || savingQc || qcInput === (state.quickconnect_url || "")}
            title={canWrite ? "" : "Réservé au Premier Admin"}
            data-testid="nas-quickconnect-save"
          >
            <Save size={13} /> Enregistrer
          </Button>
          <Button
            size="sm"
            className="gap-1.5 bg-[#0078D4] hover:bg-[#005ea6] text-white"
            disabled={!state.quickconnect_url}
            onClick={() => { if (state.quickconnect_url) window.open(state.quickconnect_url, "_blank", "noopener,noreferrer"); }}
            data-testid="nas-quickconnect-open"
          >
            <ExternalLink size={13} /> Ouvrir le dossier de backups sur le NAS
          </Button>
        </div>
      </div>

      {/* Config technique repliable */}
      <div className="mx-6 mb-6 rounded-lg overflow-hidden"
           style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.10)" }}>
        <button
          type="button"
          onClick={() => setShowTech((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition"
          data-testid="nas-tech-toggle"
        >
          <div className="flex items-center gap-2">
            <Wrench size={14} className="text-muted-foreground" />
            <span className="text-sm font-medium">Configuration technique</span>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              — clé secrète, endpoint, script curl
            </span>
          </div>
          {showTech ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {showTech && (
          <div className="px-4 pb-4 pt-1 space-y-4 border-t border-vertec-borderDark/40">
            {/* Avertissement preview — spec 2026-08-01 */}
            {isPreviewHost && (
              <div className="rounded p-3 flex items-start gap-2"
                   style={{ background: "#17171B", border: "1px solid #26262C", borderLeft: "3px solid #EF4444" }}
                   data-testid="nas-preview-warning">
                <AlertOctagon size={16} className="text-[#EF4444] mt-0.5 shrink-0" />
                <div className="text-xs text-[#FCA5A5]">
                  <strong className="block mb-0.5 text-[#EF4444]">Vous êtes sur l&apos;environnement <em>preview</em>.</strong>
                  Toute clé générée ici produira un script qui pointe vers ce domaine —
                  or celui-ci est bloqué en amont par le WAF (HTTP 403 avant même d&apos;atteindre Le Desk).
                  <br />
                  <strong>Reconnectez-vous depuis <code>https://app.vert-tec.com</code></strong> avant de régénérer la clé et le script pour le NAS.
                </div>
              </div>
            )}

            {/* Endpoint */}
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Endpoint URL <span className="normal-case tracking-normal text-muted-foreground">(à insérer dans le script Synology)</span>
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 text-xs bg-vertec-borderDark/40 px-3 py-2 rounded font-mono break-all">
                  <Secret value={endpointFull || state.endpoint_path} superAdmin={canWrite} testId="nas-secret-webhook" />
                </code>
                <Button variant="outline" size="sm" className="gap-1.5 border-vertec-borderDark"
                        onClick={() => copy(endpointFull, "Endpoint copié")}
                        data-testid="nas-endpoint-copy">
                  <Copy size={12} /> Copier
                </Button>
              </div>
            </div>

            {/* Clé secrète */}
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Clé secrète NAS (header <code className="text-[10px]">X-NAS-Secret</code>)
              </Label>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <code className="flex-1 text-xs bg-vertec-borderDark/40 px-3 py-2 rounded font-mono min-w-[200px]">
                  {state.secret_hint || <span className="text-muted-foreground italic">Aucune clé — cliquez sur Générer</span>}
                </code>
                <Button
                  onClick={rotate}
                  disabled={!canWrite || rotating || isPreviewHost}
                  variant="outline" size="sm"
                  className="gap-1.5 border-vertec-borderDark"
                  data-testid="nas-secret-rotate"
                  title={
                    !canWrite
                      ? "Zone de danger — réservée au Premier Admin"
                      : isPreviewHost
                        ? "Désactivé sur l'environnement preview — reconnectez-vous depuis app.vert-tec.com"
                        : ""
                  }
                >
                  <RefreshCw size={12} className={rotating ? "animate-spin" : ""} />
                  {state.has_secret ? "Régénérer" : "Générer la clé"}
                </Button>
              </div>
              {state.secret_created_at && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Clé créée le {fmtDate(state.secret_created_at)} · La régénération invalide immédiatement l&apos;ancienne (sans délai de grâce).
                </p>
              )}
            </div>

            {/* Spec iter317 — script courant complet (clé + chemin injectés) */}
            <div id="nas-script-block" data-testid="nas-script-block">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Script v{script?.version ?? state.script_version_latest} <span className="normal-case tracking-normal">(à coller dans le Planificateur Synology)</span>
                </Label>
                <Button variant="outline" size="sm" className="gap-1.5 border-vertec-borderDark"
                        onClick={() => copy(script?.script, "Script copié")} disabled={!script?.script}
                        data-testid="nas-script-copy">
                  <Copy size={12} /> Copier
                </Button>
              </div>
              <pre className="text-[11px] font-mono leading-relaxed px-3 py-2.5 overflow-y-auto whitespace-pre"
                   style={{ background: "#151517", border: "1px solid #26262a", borderRadius: 8, maxHeight: 320, color: "#c8c8cc" }}
                   data-testid="nas-script-content">{script?.script || "Chargement du script…"}</pre>
              {script && !script.has_plain_secret && (
                <p className="text-[10px] mt-1" style={{ color: "#F5C842" }} data-testid="nas-script-no-secret">
                  La clé actuelle a été générée avant cette version et ne peut pas être ré-affichée : cliquez sur « Régénérer » pour obtenir un script avec la clé injectée.
                </p>
              )}
              <div className="mt-3 flex flex-col items-start gap-2">
                <Button variant="outline" size="sm" className="gap-1.5 border-vertec-borderDark" onClick={runTest} disabled={testing}
                        data-testid="nas-test-report">
                  {testing ? <RefreshCw size={12} className="animate-spin" /> : <Terminal size={12} />}
                  {testing ? "Test en cours…" : "Tester la réception"}
                </Button>
                {testResult && (
                  <div className="flex items-start gap-2 rounded-md px-3 py-2 text-[11.5px] w-full"
                       style={testResult.ok ? { background: "#1a2a1e", border: "1px solid #2d5c3a", color: "#9fd9bb" } : { background: "#2a1a1a", border: "1px solid #5c2d2d", color: "#d99b9b" }}
                       data-testid={testResult.ok ? "nas-test-success" : "nas-test-failure"}>
                    {testResult.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <AlertOctagon size={14} className="mt-0.5 shrink-0" />}
                    <div>
                      {testResult.ok
                        ? <>Chaîne validée — le backend reçoit et traite correctement les rapports (version détectée : v{testResult.detected_version}).</>
                        : <><strong>Échec — étape « {({ secret: "clé secrète", payload: "payload", database: "base de données", http: "appel HTTP" })[testResult.step] || testResult.step} »</strong> (HTTP {testResult.http}) : {testResult.detail}</>}
                      {testResult.steps?.length > 0 && <div className="opacity-70 mt-0.5">Étapes OK : {testResult.steps.join(" → ")}</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Fréquence recommandée */}
            <div className="text-xs text-muted-foreground rounded p-3"
                 style={{ background: "#17171B", border: "1px dashed rgba(245,200,66,0.4)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Terminal size={12} className="text-[#F5C842]" />
                <strong className="text-[#F5C842] text-[11px] uppercase tracking-widest">Fréquence recommandée</strong>
              </div>
              Programmez la tâche planifiée du NAS à <strong>toutes les 6 heures</strong>. C&apos;est le compromis entre fraîcheur de l&apos;indicateur "En ligne" et charge réseau minimale.
            </div>

            {/* Diagnostic : rejets récents (spec 2026-08-01) */}
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                <AlertOctagon size={12} className="text-[#EF4444]" />
                Diagnostic — 10 dernières tentatives rejetées
              </Label>
              {(!state.recent_rejections || state.recent_rejections.length === 0) ? (
                <p className="text-[11px] text-muted-foreground italic mt-1">
                  Aucune tentative rejetée enregistrée. Si le badge reste bloqué sur « Aucun rapport reçu »
                  et que cette liste reste vide, c&apos;est qu&apos;<strong>aucune requête n&apos;atteint Le Desk</strong>
                  (à vérifier côté NAS : pare-feu sortant, DNS, chemin réseau vers l&apos;URL de production).
                </p>
              ) : (
                <div className="mt-1 rounded border border-vertec-borderDark/60 overflow-hidden">
                  <table className="w-full text-[11px]" data-testid="nas-rejections-table">
                    <thead>
                      <tr className="bg-vertec-borderDark/40 text-muted-foreground uppercase tracking-wider text-[9px]">
                        <th className="text-left px-2 py-1.5">Quand</th>
                        <th className="text-left px-2 py-1.5">Raison</th>
                        <th className="text-left px-2 py-1.5">IP source</th>
                        <th className="text-left px-2 py-1.5">Clé envoyée</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {state.recent_rejections.map((r, i) => (
                        <tr key={i} className="border-t border-vertec-borderDark/40">
                          <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">
                            {fmtRelative(r.at)}
                          </td>
                          <td className="px-2 py-1.5">
                            <span style={{
                              color: r.reason === "invalid_payload" ? "#F5C842" : "#EF4444",
                            }}>
                              {r.reason === "missing_secret" && "Header absent (401)"}
                              {r.reason === "invalid_secret" && "Clé invalide (401)"}
                              {r.reason === "invalid_payload" && "Payload invalide (422)"}
                              {!["missing_secret","invalid_secret","invalid_payload"].includes(r.reason) && r.reason}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-muted-foreground">{r.source_ip || "—"}</td>
                          <td className="px-2 py-1.5 text-muted-foreground">
                            {r.header_present ? (r.header_last4 ? `•••${r.header_last4}` : "présent") : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[10px] text-muted-foreground mt-1.5 italic">
                Si vous voyez ici des lignes « Clé invalide » avec les 4 derniers caractères qui ne correspondent pas à
                {state.secret_hint ? <> <code className="text-[10px]">{state.secret_hint}</code></> : " votre clé actuelle"},
                le script NAS utilise une ancienne clé — régénérez ci-dessus et recopiez le script.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modale one-shot clé secrète */}
      <Dialog open={!!oneShot} onOpenChange={(v) => { if (!v) setOneShot(null); }}>
        <DialogContent className="max-w-2xl" data-testid="nas-secret-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound size={20} className="text-[#F5C842]" />
              {oneShot?.is_regeneration ? "Nouvelle clé secrète NAS" : "Clé secrète NAS générée"}
            </DialogTitle>
            <DialogDescription>
              Copiez cette clé maintenant — elle ne sera plus jamais affichée. Le hash est stocké côté Le Desk pour vérifier les rapports entrants ; nous ne conservons pas la valeur en clair.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md p-3"
                 style={{ background: "#17171B", border: "1px solid #26262C", borderLeft: "3px solid #F5C842" }}>
              <Label className="text-[10px] uppercase tracking-widest text-[#F5C842]">Clé secrète (affichage unique)</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 text-sm bg-vertec-borderDark/60 px-3 py-2 rounded font-mono break-all"
                      data-testid="nas-secret-plain">
                  {oneShot?.secret}
                </code>
                <Button variant="outline" size="sm" className="gap-1.5 border-vertec-borderDark"
                        onClick={() => copy(oneShot?.secret, "Clé copiée")}
                        data-testid="nas-secret-plain-copy">
                  <Copy size={12} /> Copier
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Script Synology prêt à coller</Label>
              <p className="text-[11px] text-muted-foreground mb-2">
                Panneau de configuration &gt; Planificateur de tâches &gt; Créer &gt; Tâche planifiée &gt; Script défini par l&apos;utilisateur. Fréquence : <strong>toutes les 6 heures</strong>.
              </p>
              <div className="flex items-center gap-2 mb-1"><span className="text-[11px] text-muted-foreground">Clé secrète :</span><Secret value={oneShot?.secret} superAdmin={canWrite} testId="nas-secret-key" /></div>
              <textarea
                readOnly
                value={canWrite && revealScript ? (oneShot?.curl_snippet || "") : (oneShot?.curl_snippet || "").replace(oneShot?.secret || "\u0000", "••••••")}
                rows={16}
                className="w-full text-[11px] font-mono bg-vertec-borderDark/60 border border-vertec-borderDark rounded px-3 py-2"
                data-testid="nas-secret-curl"
              />
              <div className="flex justify-end mt-2 gap-2">
                {canWrite && <Button variant="outline" size="sm" className="gap-1.5 border-vertec-borderDark" onClick={() => setRevealScript((v) => !v)} aria-pressed={revealScript} data-testid="nas-secret-curl-eye">{revealScript ? "Masquer la clé" : "Révéler la clé"}</Button>}
                <Button variant="outline" size="sm" className="gap-1.5 border-vertec-borderDark"
                        onClick={() => copy(oneShot?.curl_snippet, "Script copié")}
                        data-testid="nas-secret-curl-copy">
                  <Copy size={12} /> Copier le script
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setOneShot(null)} data-testid="nas-secret-close">
              J&apos;ai enregistré la clé — Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
